// Flat top bar.
//
//   NEXUS · [server-switcher]   Command Center · Analytics · Sleeves · Accounts · System      charlton · Sign out
//
// System subsumes Config, Servers, and Admin Users.

import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { vpsApi } from "../api/vps";

export function NavBar() {
  const { username, activeVpsId, enterVps, logout } = useAuth();
  const nav = useNavigate();

  const rosterQ = useQuery({
    queryKey: ["vps", "list"],
    queryFn: vpsApi.list,
    staleTime: 30_000,
  });
  const roster = rosterQ.data ?? [];

  return (
    <nav className="nav">
      <div className="nav-brand">NEXUS</div>

      <ServerSwitcher
        roster={roster}
        activeVpsId={activeVpsId}
        onSwitch={async (id) => {
          await enterVps(id);
          nav("/desk");
        }}
      />

      {activeVpsId !== null && (
        <>
          <NavLink to="/desk" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            Command Center
          </NavLink>
          <NavLink to="/analytics" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            Analytics
          </NavLink>
          <NavLink to="/sleeves" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            Sleeves
          </NavLink>
          <NavLink to="/setup" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            Accounts
          </NavLink>
        </>
      )}
      <NavLink to="/system" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
        System
      </NavLink>

      <div className="nav-spacer" />

      <NavLink to="/account" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
        {username}
      </NavLink>
      <button onClick={() => void logout()}>Sign out</button>
    </nav>
  );
}

interface RosterRow {
  id: number;
  label: string;
}

function ServerSwitcher({
  roster,
  activeVpsId,
  onSwitch,
}: {
  roster: RosterRow[];
  activeVpsId: number | null;
  onSwitch: (id: number) => Promise<void>;
}) {
  // 0 servers — surface a CTA, the only way out is the wizard.
  if (roster.length === 0) {
    return (
      <NavLink to="/onboarding" className="pill ghost" style={{ textDecoration: "none" }}>
        + Set up your server
      </NavLink>
    );
  }
  // 1 server — just a chip, no switcher (the choice is moot).
  if (roster.length === 1) {
    const r = roster[0];
    return (
      <span className="pill" title={`active server: ${r.label}`}>
        {r.label}
      </span>
    );
  }
  // 2+ — dropdown that issues an enter on change.
  return (
    <select
      value={activeVpsId ?? ""}
      onChange={(e) => {
        const id = Number(e.target.value);
        if (Number.isFinite(id)) void onSwitch(id);
      }}
      title="active server"
      className="nav-switcher"
    >
      {activeVpsId === null && <option value="">— pick a server —</option>}
      {roster.map((r) => (
        <option key={r.id} value={r.id}>
          {r.label}
        </option>
      ))}
    </select>
  );
}
